import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/components/Dialog';
import { PillButton } from '@/shared/components/PillButton';
import { createTextStroke } from '@/styles/textStroke';

const STORAGE_KEY = 'ludoka.notice.privateFreePointsAnnouncement.dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    // localStorage unavailable (private browsing, disabled storage) — treat as not dismissed
    return false;
  }
}

/**
 * One-time announcement shown on app open explaining that private free games
 * no longer award SOKA points, and nudging players toward public games.
 *
 * Persists dismissal in localStorage so it appears at most once per browser.
 */
export function PointsAnnouncementDialog() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(() => !readDismissed());

  const handleClose = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      // localStorage unavailable — dismiss for this session only
    }
    setIsOpen(false);
  }, []);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      variant="green"
      title={t('announcements.privateFreePoints.title')}
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-700 leading-snug">
          {t('announcements.privateFreePoints.intro')}
        </p>

        <p className="text-base font-bold text-green-700 leading-snug text-center">
          {t('announcements.privateFreePoints.callToAction')}
        </p>

        <ul className="flex flex-col gap-2">
          <li className="text-sm font-semibold text-amber-900 bg-amber-50 border-l-4 border-amber-500 rounded-md px-3 py-2 leading-snug">
            {t('announcements.privateFreePoints.bonus')}
          </li>
          <li className="text-sm font-medium text-gray-800 bg-gray-50 border-l-4 border-gray-400 rounded-md px-3 py-2 leading-snug">
            {t('announcements.privateFreePoints.paidUnchanged')}
          </li>
        </ul>

        <PillButton
          variant="green"
          onClick={handleClose}
          className="w-full h-12 mt-1"
          icon={
            <span
              className="font-mind-boggle text-[#F2F2F2] leading-none text-lg whitespace-nowrap"
              style={{ fontWeight: 1000, ...createTextStroke('1.5px') }}
            >
              {t('announcements.privateFreePoints.dismiss')}
            </span>
          }
        />
      </div>
    </Dialog>
  );
}

export default PointsAnnouncementDialog;
